import { safeDefine } from "@breuni-ui/safe-define";
import { getBaseUrl } from "../utils/getBaseUrl";
import type KombiModulCarouselContainer from "../carousel/KombiModulCarouselContainer";
import type { LoadBestandteileDetails } from "../carousel/KombiModulCarouselContainer";
import type { KombiModulModal } from "../modal/KombiModulModal";
import {
  type Client,
  createClient,
  createConfig,
} from "../../_generated/client";
import type {
  GetRecoPythiaKombiModulRenderAppBestandteileData,
  GetRecoPythiaKombiModulRenderBestandteileData,
  GetRecoPythiaKombiModulRenderOutfitsData,
} from "../../_generated";
import {
  getRecoPythiaKombiModulRenderBestandteile,
  getRecoPythiaKombiModulRenderOutfits,
} from "../../_generated";
import { ENDPOINT_PATH_RECO_PYTHIA_KOMBI_MODUL_RENDER_APP_BESTANDTEILE } from "../../_generated/endpoint-paths.gen";
import cancelableRequest from "./cancelableRequest";
import { openNativeDialog } from "@mobile-app/native-dialog";

class KombiModulLoader extends HTMLElement {
  private static readonly gridSelector = '[data-id="reco-kombi-modul-grid"]';
  private pythiaClient: Client | undefined;
  private currentFarbId: string | null | undefined;
  private modalContainer: Element | null | undefined;
  private carouselContainer: KombiModulCarouselContainer | null | undefined;
  private useBottomSheet = false;
  private hidePreise = false;
  private similarity = "v1";
  private currentlyRunningFetch: {
    promise: Promise<{
      data: string | undefined;
      response: Response;
    }>;
    abort: () => void;
  } | null = null;

  public constructor() {
    super();
  }

  public static get observedAttributes(): string[] {
    return ["color-id"];
  }
  public connectedCallback(): void {
    const countryUrlPrefix = this.getAttribute("country-url-prefix");
    if (countryUrlPrefix !== null && countryUrlPrefix.length > 0) {
      const baseUrl = getBaseUrl(countryUrlPrefix);
      this.pythiaClient = createClient(
        createConfig({
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Accept: "text/html",
          },
          baseUrl,
        }),
      );
    }
    this.useBottomSheet = this.getAttribute("use-bottom-sheet") === "true";
    this.hidePreise = this.getAttribute("hide-preise") === "true";
    const sim = this.getAttribute("similarity");
    this.similarity =
      sim === null || typeof sim === "undefined" ? this.similarity : sim;
  }

  public attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (name === "color-id") {
      if (this.currentlyRunningFetch !== null) {
        this.currentlyRunningFetch.abort();
      }
      this.currentFarbId = newValue;
      if (oldValue !== newValue) {
        if (newValue === null) {
          this.removeChildContent();
        } else {
          this.currentlyRunningFetch = this.getOutfitRenderRequest(newValue);
          const timeoutId = setTimeout(() => {
            this.currentlyRunningFetch?.abort();
          }, 3000);
          this.currentlyRunningFetch.promise
            .then(({ data: outfits, response }) => {
              const validResponse =
                response.status === 200 && typeof outfits !== "undefined";
              clearTimeout(timeoutId);
              if (!validResponse) {
                this.removeChildContent();
                return;
              }
              if (newValue === this.currentFarbId) {
                this.appendChildWithContent(outfits);
              }
            })
            .finally(() => {
              this.currentlyRunningFetch = null;
            })
            .catch((_error: unknown) => {
              this.removeChildContent();
            });
        }
      }
    }
  }

  private getOutfitRenderRequest(farbId: string): {
    promise: Promise<{ data: string | undefined; response: Response }>;
    abort: () => void;
  } {
    const queryParams: GetRecoPythiaKombiModulRenderOutfitsData["query"] = {
      farbId,
    };
    return cancelableRequest(
      this.pythiaClient,
      getRecoPythiaKombiModulRenderOutfits,
      queryParams,
    );
  }

  private handleLoadBestandteile(
    ev: CustomEvent<LoadBestandteileDetails>,
  ): void {
    if (this.useBottomSheet) {
      this.loadBestandteileForNativeDialog(ev);
    } else {
      this.loadBestandteileForModal(ev);
    }
  }

  private loadBestandteileForNativeDialog(
    ev: CustomEvent<LoadBestandteileDetails>,
  ): void {
    const queryParams: GetRecoPythiaKombiModulRenderAppBestandteileData["query"] =
      {
        farbId: ev.detail.farbId,
        relativepath: ev.detail.relativePath,
        slot: ev.detail.slot,
        ispureluxury: ev.detail.isPureLuxury,
        hidepreise: this.hidePreise,
      };
    const options = {
      url: ENDPOINT_PATH_RECO_PYTHIA_KOMBI_MODUL_RENDER_APP_BESTANDTEILE,
      query: queryParams,
    };
    const url = [
      this.pythiaClient?.getConfig().baseUrl,
      this.pythiaClient?.buildUrl(options) ?? "",
    ].join("");

    openNativeDialog({
      url,
      height: 0.9,
      draggable: false,
      dismissible: true,
    });
  }

  private loadBestandteileForModal(
    ev: CustomEvent<LoadBestandteileDetails>,
  ): void {
    const queryParams: GetRecoPythiaKombiModulRenderBestandteileData["query"] =
      {
        farbId: ev.detail.farbId,
        relativepath: ev.detail.relativePath,
        slot: ev.detail.slot,
        ispureluxury: ev.detail.isPureLuxury,
        hidepreise: this.hidePreise,
        similarityVersion: this.similarity,
      };
    getRecoPythiaKombiModulRenderBestandteile({
      client: this.pythiaClient,
      query: queryParams,
    })
      .then(({ data: renderedBestandteile }) => {
        if (typeof renderedBestandteile === "undefined") {
          return;
        }
        const bestandteileFragment = document
          .createRange()
          .createContextualFragment(renderedBestandteile);
        const modal = this.modalContainer?.querySelector(
          "#reco-kombi-modul-modal",
        );
        if (modal !== null && typeof modal !== "undefined") {
          modal.replaceWith(bestandteileFragment);
        } else {
          this.modalContainer?.appendChild(bestandteileFragment);
        }

        window.requestAnimationFrame(() => {
          this.modalContainer
            ?.querySelector<KombiModulModal>("#reco-kombi-modul-modal")
            ?.show();
        });
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch((_unknown: unknown) => {});
  }

  private appendChildWithContent(renderedOutfits: string): void {
    const outfitsFragment = document
      .createRange()
      .createContextualFragment(renderedOutfits);
    const gridContainer = this.querySelector<HTMLDivElement>(
      KombiModulLoader.gridSelector,
    );
    window.requestAnimationFrame(() => {
      if (gridContainer) {
        gridContainer.replaceWith(outfitsFragment);
      } else {
        this.appendChild(outfitsFragment);
      }
      this.carouselContainer = this.querySelector<KombiModulCarouselContainer>(
        "reco-kombi-modul-carousel-container",
      );
      this.modalContainer = this.querySelector(
        "reco-kombi-modul-modal-container",
      );
      if (this.carouselContainer !== null) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        this.carouselContainer.addEventListener("load:bestandteile", ((
          ev: CustomEvent<LoadBestandteileDetails>,
        ) => {
          this.handleLoadBestandteile(ev);
        }) as EventListener);
      }
    });
  }

  private removeChildContent(): void {
    const gridContainer = this.querySelector<HTMLDivElement>(
      KombiModulLoader.gridSelector,
    );
    // https://developers.google.com/web/fundamentals/performance/rendering/optimize-javascript-execution#use_requestanimationframe_for_visual_changes
    window.requestAnimationFrame(() => {
      if (gridContainer) {
        this.removeChild(gridContainer);
      }
    });
  }
}

safeDefine("reco-kombi-modul-loader", KombiModulLoader);
